import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

export function PqSyncStatus({ record, poll = false, prefix = "pq-sync" }) {
  const { t } = useLanguage();
  const [latest, setLatest] = useState(record);
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const id = record?.id;
  useEffect(() => { setLatest(record); }, [record]);
  useEffect(() => {
    if (!id || (!poll && !open)) return;
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/procurement-quotes/${id}/sync`);
        if (active) { setLatest(data.quote); setEvents(data.events); }
      } catch (error) { if (active && open) toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
    };
    load();
    const timer = setInterval(load, 4000);
    return () => { active = false; clearInterval(timer); };
  }, [id, poll, open]);
  if (!id) return null;
  const state = latest?.sync_status || "synced";
  const attention = ["retry", "conflict", "missing", "uncertain", "review"].includes(state);
  return <>
    <button type="button" onClick={() => setOpen(true)} data-testid={`${prefix}-${id}`}
      className={`text-[10px] text-left hover:underline ${attention ? "text-amber-700" : "text-muted-foreground"}`}>
      {t(`pq.syncState.${state}`)}
    </button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`${prefix}-detail-${id}`}>
        <DialogHeader><DialogTitle>{t("pq.syncDetails")}</DialogTitle><DialogDescription>{latest?.item_number}</DialogDescription></DialogHeader>
        <p data-testid={`${prefix}-state-${id}`}>{t(`pq.syncState.${state}`)}</p>
        {latest?.sync_error && <p className="text-sm text-amber-700" data-testid={`${prefix}-error-${id}`}>{latest.sync_error}</p>}
        {Object.entries(latest?.sync_conflicts || {}).map(([field, values]) => <div key={field} className="border p-2 text-xs space-y-1" data-testid={`${prefix}-conflict-${id}-${field}`}>
          <strong>{field}</strong><p>APP: {String(values.app ?? "")}</p><p>FileMaker: {String(values.filemaker ?? "")}</p>
        </div>)}
        {["pending", "retry"].includes(state) && <Button variant="outline" data-testid={`${prefix}-retry-${id}`} onClick={async () => {
          try { await api.post(`/procurement-quotes/${id}/sync/retry`); toast.success(t("pq.retryQueued")); }
          catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
        }}>{t("pq.retrySync")}</Button>}
        <div className="space-y-2" data-testid={`${prefix}-events-${id}`}>
          {events.map((event) => <div key={event.id} className="border-t pt-2 text-xs" data-testid={`${prefix}-event-${event.id}`}>
            <p className="text-muted-foreground">{new Date(event.at).toLocaleString()} · {event.actor} · {event.direction} · {event.result}</p>
            {Object.entries(event.changes || {}).map(([field, value]) => <p key={field}>{field}: {JSON.stringify(value.before)} → {JSON.stringify(value.after)}</p>)}
            {event.message && <p>{event.message}</p>}
          </div>)}
        </div>
      </DialogContent>
    </Dialog>
  </>;
}
